/**
 * Promotion flow (Stage 3): session outputs → system items with provenance.
 *
 * When a user promotes session items:
 * 1. Generate promotion_cohort_id
 * 2. Insert promotion_events row (event_type = 'promotion'), capture event id
 * 3. Update items with promotion_event_id, promotion_cohort_id, source_session_id, promoted_by, promoted_at
 *
 * Promotion does not bypass normal intake/shaping; it records provenance.
 * App-linked and isolated sessions use the same promotion model.
 */

import { getSafeSupabaseClient, getCurrentWorkspaceId, generateUUID } from './supabaseSync';
import { updateRecord } from './supabaseSync';
import { useAuditStore } from '../stores/prioritization/auditStore';
import type { Item } from '../types/prioritization';

const EVENTS_TABLE = 'promotion_events';
const ITEMS_TABLE = 'items';

export interface PromoteSessionItemsResult {
  success: boolean;
  error?: string;
  promotionEventId?: string;
  promotionCohortId?: string;
  itemCount?: number;
  /** IDs that were updated with provenance. */
  succeededIds?: string[];
  /** IDs that were skipped (e.g. not found in items table). */
  skippedIds?: string[];
}

/**
 * Promote session items: insert promotion event and set provenance on items.
 * Caller should refresh items store after success (e.g. fetchFromSupabase).
 */
export async function promoteSessionItems(
  sessionId: string,
  itemIds: string[],
  promotedBy: string
): Promise<PromoteSessionItemsResult> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { success: false, error: 'Supabase not configured' };
  }
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) {
    return { success: false, error: 'No workspace selected' };
  }
  if (itemIds.length === 0) {
    return { success: false, error: 'No items to promote' };
  }

  const promotionCohortId = generateUUID();
  const now = new Date().toISOString();

  const succeededIds: string[] = [];
  const skippedIds: string[] = [];

  const eventRow = {
    workspace_id: workspaceId,
    event_type: 'promotion',
    occurred_at: now,
    actor_id: promotedBy,
    payload: {
      promotion_cohort_id: promotionCohortId,
      source_session_id: sessionId,
      item_ids: itemIds,
      item_count: itemIds.length,
    },
  };

  const { data: insertedEvent, error: insertError } = await supabase
    .from(EVENTS_TABLE)
    .insert(eventRow)
    .select('id')
    .single();

  if (insertError || !insertedEvent?.id) {
    console.error('[promotion] Failed to insert promotion_events:', insertError);
    return { success: false, error: insertError?.message ?? 'Failed to create promotion event', succeededIds, skippedIds };
  }

  const promotionEventId = insertedEvent.id as string;
  const provenance: Partial<Item> = {
    promotionEventId,
    promotionCohortId,
    sourceSessionId: sessionId,
    promotedBy,
    promotedAt: new Date(),
  };

  for (const itemId of itemIds) {
    const result = await updateRecord<Item>(ITEMS_TABLE, itemId, provenance);
    if (result.error) {
      console.warn(`[promotion] Failed to update item ${itemId}:`, result.error);
      skippedIds.push(itemId);
    } else {
      succeededIds.push(itemId);
      try {
        useAuditStore.getState().logEvent({
          type: 'item_promoted',
          itemId,
          actorId: promotedBy,
          actorRole: 'promoter',
          note: `Promoted from session ${sessionId}`,
          details: { sessionId, promotionEventId, promotionCohortId },
        } as any);
      } catch (_) { /* best-effort */ }
    }
  }

  return {
    success: true,
    promotionEventId,
    promotionCohortId,
    itemCount: itemIds.length,
    succeededIds,
    skippedIds,
  };
}
